import { PageHero } from '@/components/PageHero';
import { TaxSavingsCalculator } from '@/components/TaxSavingsCalculator';
import { CtaBanner } from '@/components/CtaBanner';
import { useSeo } from '@/components/Seo';
import { useLang } from '@/i18n/LangContext';
import { TAX_YEAR } from '@/data/taxSavings3a';

const faq = [
  {
    q: 'Quanto si risparmia fiscalmente con il pilastro 3a?',
    a: 'Dipende dal reddito imponibile, dal comune di domicilio, dal cantone e dall’importo versato. Il calcolatore mostra una stima orientativa per una persona sola senza figli a carico, senza imposta ecclesiastica, usando lo stesso imponibile per Confederazione e cantone.',
  },
  {
    q: 'Il calcolo vale anche per chi ha un permesso B, L o G?',
    a: 'Per il permesso B il risultato rappresenta la differenza nella tassazione ordinaria. L’eventuale rimborso dell’imposta alla fonte dipende dalla tassazione ordinaria successiva e dalla situazione personale. Il calcolatore non copre i permessi L o G.',
  },
  {
    q: 'Il comune cambia davvero il risultato?',
    a: 'Sì. In Svizzera la fiscalità cambia tra cantoni e comuni. Puoi cercare il comune per nome o CAP e il calcolo applica le tariffe dell’AFC per il comune scelto.',
  },
];

export default function TaxSavingsPage() {
  const { t } = useLang();
  useSeo({
    title: 'Calcola il risparmio fiscale del pilastro 3a in Svizzera',
    description: 'Calcolatore risparmio fiscale pilastro 3a: stima quanto puoi risparmiare in Svizzera inserendo reddito imponibile, CAP o comune di domicilio e importo versato.',
    keywords: ['calcola risparmio fiscale 3a', 'risparmio fiscale pilastro 3a svizzera', 'deduzione 3a comune domicilio', 'calcolatore terzo pilastro', 'rimborso imposta alla fonte 3a'],
    faq,
    schemaType: 'FinancialProduct',
  });

  return (
    <div data-testid="tax-savings-page">
      <PageHero
        eyebrow="Pilastro 3a"
        title="Calcola il tuo risparmio fiscale 3a"
        summary="Stima il beneficio fiscale di un versamento nel pilastro 3a con le tariffe AFC, considerando reddito imponibile, comune di domicilio e situazione fiscale."
        image="/assets/area-previdenza.webp"
        imageAlt="Consulenza previdenziale e risparmio fiscale in Svizzera"
        breadcrumbs={[{ label: t('breadcrumbHome'), href: '/' }, { label: 'Calcola il risparmio fiscale' }]}
      />

      <section className="mx-auto max-w-site px-5 md:px-8 pt-16 md:pt-24">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-12 md:mb-16">
          <div className="lg:col-span-7">
            <p className="text-sm uppercase tracking-[0.22em] font-bold text-primary mb-4">Calcolatore 3a Svizzera</p>
            <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-balance">Quanto puoi risparmiare sulle imposte versando nel pilastro 3a?</h1>
            <div className="mt-6 space-y-4 text-base md:text-lg leading-relaxed text-foreground/72">
              <p>
                Il pilastro 3a non è solo previdenza: per molte persone è anche uno degli strumenti più immediati per ridurre il reddito imponibile. Inserendo il reddito imponibile, il CAP o il comune di domicilio e l’importo annuo versato puoi visualizzare una stima del risparmio fiscale.
              </p>
              <p>
                Il risultato cambia in base alla fiscalità locale. Per il permesso B la stima mostra l’effetto sulla tassazione ordinaria successiva, che può differire dal rimborso effettivo dell’imposta alla fonte.
              </p>
            </div>
          </div>
          <aside className="lg:col-span-5 rounded-2xl border border-border bg-brand-pale/55 p-6 md:p-7">
            <h2 className="font-heading text-2xl font-bold tracking-tight mb-4">Cosa puoi capire subito</h2>
            <ul className="space-y-3 text-sm leading-relaxed text-foreground/72">
              <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />Quanto pesa fiscalmente il tuo versamento 3a.</li>
              <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />Se sei vicino al massimo deducibile annuale.</li>
              <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />Come cambia il risultato in base al comune di domicilio.</li>
              <li className="flex gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />Se ha senso approfondire con una consulenza previdenziale.</li>
            </ul>
          </aside>
        </div>

        <TaxSavingsCalculator source="dedicated-page" />
      </section>

      <section className="mx-auto max-w-site px-5 md:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-3 gap-6">
          <article className="rounded-2xl border border-border bg-white p-6 md:p-7">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-3">01</p>
            <h2 className="font-heading text-2xl font-bold tracking-tight mb-3">Perché il 3a riduce le imposte</h2>
            <p className="text-sm leading-relaxed text-foreground/70">
              I versamenti deducibili nel pilastro 3a abbassano il reddito imponibile. Più alta è l’aliquota marginale, più il beneficio fiscale potenziale diventa interessante rispetto all’importo versato.
            </p>
          </article>
          <article className="rounded-2xl border border-border bg-white p-6 md:p-7">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-3">02</p>
            <h2 className="font-heading text-2xl font-bold tracking-tight mb-3">Comune e permesso contano</h2>
            <p className="text-sm leading-relaxed text-foreground/70">
              In Svizzera la tassazione cambia tra cantoni e comuni. Inoltre, per chi è soggetto a imposta alla fonte, il vantaggio può arrivare tramite rimborso, rettifica o tassazione ordinaria ulteriore.
            </p>
          </article>
          <article className="rounded-2xl border border-border bg-white p-6 md:p-7">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-bold mb-3">03</p>
            <h2 className="font-heading text-2xl font-bold tracking-tight mb-3">Il risparmio non basta</h2>
            <p className="text-sm leading-relaxed text-foreground/70">
              Un buon 3a deve essere coerente con obiettivi, rischio, liquidità, protezione famigliare e orizzonte temporale. Il beneficio fiscale è solo il primo dato da guardare.
            </p>
          </article>
        </div>

        <div className="mt-12 rounded-2xl bg-brand-deep text-white p-6 md:p-10">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <h2 className="font-heading text-3xl font-bold tracking-tight">Calcolo fiscale 3a: da stima a decisione</h2>
              <p className="mt-4 text-white/72 leading-relaxed">
                Il calcolatore confronta le imposte sul reddito prima e dopo la deduzione 3a con le tariffe AFC {TAX_YEAR}. La stima assume una persona sola senza figli a carico, senza imposta ecclesiastica, e usa lo stesso reddito imponibile per Confederazione e cantone.
              </p>
            </div>
            <div className="lg:col-span-5 rounded-xl bg-white/10 border border-white/15 p-5">
              <p className="text-sm font-bold mb-2">Fonti dei dati</p>
              <p className="text-sm text-white/70 leading-relaxed">
                CAP e comuni: registro delle località swisstopo. Imposte: calcolatore dell’Amministrazione federale delle contribuzioni. Se il servizio AFC non è disponibile, il sito non mostra un importo stimato.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-6">Domande frequenti sul risparmio fiscale 3a</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {faq.map((item) => (
              <article key={item.q} className="rounded-xl border border-border bg-white p-5">
                <h3 className="font-heading text-xl font-bold tracking-tight mb-3">{item.q}</h3>
                <p className="text-sm leading-relaxed text-foreground/70">{item.a}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-site px-5 md:px-8">
        <CtaBanner
          title="Vuoi verificare il risultato sul tuo caso concreto?"
          text="Il calcolatore è orientativo: in consulenza verifichiamo deducibilità, comune, situazione fiscale, prodotto 3a e coerenza con il piano previdenziale."
          label="Richiedi una verifica 3a"
        />
      </div>
    </div>
  );
}
